import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, Zap, ArrowRight, Ruler } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { PAGE_LIST, GROUPS } from "@/lib/landingpages";
import FitCalculator from "@/components/calculators/FitCalculator";

const GENERAL_FAQ = [
  { q: "Was kann ich mit PasstDasDurch.de prüfen?", a: "Du kannst prüfen, ob ein Möbelstück oder Gerät durch eine Tür, einen Flur, einen Aufzug oder ein Treppenhaus passt. Der Rechner gibt eine ehrliche Einschätzung mit Hinweisen." },
  { q: "Wie viel Spielraum sollte ich einplanen?", a: "Plane mindestens 3 bis 5 cm ein. Möbel lassen sich selten exakt gerade durchschieben." },
  { q: "Gibt der Rechner eine Garantie?", a: "Nein. Der Rechner gibt eine praktische Orientierung. Miss vor dem Transport immer selbst nach." },
  { q: "Warum ist das Treppenhaus schwerer zu berechnen?", a: "Treppenhäuser haben Kurven, Podeste, Geländer und Türrahmen. Diese lassen sich nur grob erfassen, deshalb bleibt das Ergebnis vorsichtig." },
  { q: "Ist das Tool kostenlos?", a: "Ja. Du kannst den Rechner kostenlos und ohne Anmeldung nutzen." },
  { q: "Werden meine Eingaben gespeichert?", a: "Nein. Die Berechnung läuft vollständig in deinem Browser. Es werden keine persönlichen Daten gespeichert." },
];

export default function HomePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const groupOrder = [GROUPS.tuer, GROUPS.eng, GROUPS.grundlagen];
  const grouped = groupOrder
    .map((g) => ({ group: g, items: PAGE_LIST.filter((p) => p.group === g) }))
    .filter((x) => x.items.length > 0);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "PasstDasDurch.de",
      url: origin || undefined,
      inLanguage: "de-DE",
      description: "Prüfe, ob dein Sofa, Schrank oder Gerät durch Tür, Flur, Aufzug oder Treppenhaus passt.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "PasstDasDurch.de",
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: origin || undefined,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: "Prüfe mit Möbelmaßen und Engstellenmaßen, ob dein Möbelstück durch Tür, Flur, Aufzug oder Treppenhaus passt. Kostenlos und ohne Anmeldung.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GENERAL_FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div>
      <Seo
        title="Passt dein Sofa durch die Tür? Möbel-Durchgang prüfen"
        description="Prüfe mit Möbelmaßen und Engstellenmaßen, ob dein Sofa, Schrank oder Gerät durch Tür, Flur, Aufzug oder Treppenhaus passt. Kostenlos und ohne Anmeldung."
      canonical={origin ? `${origin}/` : undefined}
      />
      <JsonLd data={jsonLd} />

      {/* Hero + Rechner above the fold */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.10), rgba(249,115,22,0.08), rgba(251,250,247,0))", height: "55%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-6 relative">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Passt dein Sofa durch die Tür?
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Gib die Maße deines Möbelstücks und der engsten Stelle ein. Der Rechner zeigt dir, ob es wahrscheinlich passt, knapp wird oder nicht passt.
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Ohne Anmeldung</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Mobil optimiert</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> Sofort geprüft</span>
            </div>
          </div>

          <div className="mt-6 lg:mt-8">
            <h2 className="sr-only">Möbel-Durchgang prüfen</h2>
            <FitCalculator />
          </div>
        </div>
      </section>

      {/* Anzeige direkt unter dem Rechner */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-4"><AdSlot variant="banner" /></div>

      {/* Use-Case-Auswahl */}
      <section id="tools" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-xl sm:text-2xl font-semibold">Welcher Fall passt zu dir?</h2>
        <p className="text-muted-foreground mt-1">Wähle deinen Anwendungsfall. Jede Seite hat eine eigene Anleitung und Beispiele.</p>
        <div className="mt-6 space-y-8">
          {grouped.map(({ group, items }) => (
            <div key={group}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">{group}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((p) => (
                  <Link key={p.slug} to={`/${p.slug}`} data-testid={`tool-link-${p.slug}`}>
                    <Card className="p-4 h-full hover:shadow-md transition-shadow border flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                          <Ruler className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-medium">{p.navLabel}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* So funktioniert es */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: "1", t: "Möbelmaße eingeben", d: "Trag Breite, Höhe und Tiefe deines Möbelstücks ein." },
            { n: "2", t: "Engstelle wählen", d: "Tür, Flur, Aufzug oder Treppenhaus mit Maßen eingeben." },
            { n: "3", t: "Ergebnis prüfen", d: "Passt, wird knapp oder passt nicht, mit konkreten Hinweisen." },
          ].map((s) => (
            <Card key={s.n} className="p-5 border">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-heading">{s.n}</span>
              <p className="font-heading font-semibold mt-3">{s.t}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Crosslink */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
        <Card className="p-5 border flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-muted-foreground flex-1">Verkaufst du das Möbelstück? Erstelle mit dem Kleinanzeigen-Generator direkt einen Beschreibungstext.</p>
          <a href="https://kleinanzeigen-generator.de" className="text-sm font-medium hover:underline shrink-0">Zum Generator</a>
        </Card>
      </div>

      {/* Anzeige zwischen Inhalt und FAQ */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6"><AdSlot variant="banner" /></div>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fragen</h2>
        <Faq items={GENERAL_FAQ} testId="home-faq-accordion" />
      </section>

      {/* Anzeige am Seitenende */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12"><AdSlot variant="banner" /></div>
    </div>
  );
}
